import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEOHead, { SITE_URL } from '../../components/feature/SEOHead';

const termsSchema = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Terms of Use — CallOfDutyNext.com',
    description:
      'Read the Terms of Use for CallOfDutyNext.com — an independent Call of Duty fan news site. Understand your rights and responsibilities when using this site.',
    url: `${SITE_URL}/terms`,
    isPartOf: { '@type': 'WebSite', name: 'CallOfDutyNext.com', url: SITE_URL },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Terms of Use', item: `${SITE_URL}/terms` },
    ],
  },
];

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="px-6 py-7">
      <h2 className="font-['Barlow_Condensed'] font-black text-white text-xl uppercase tracking-widest mb-4">
        {title}
      </h2>
      <div className="text-[#888] text-sm leading-relaxed space-y-3">{children}</div>
    </div>
  );
}

export default function TermsPage() {
  return (
    <div className="bg-[#111] min-h-screen">
      <SEOHead
        title="Terms of Use — CallOfDutyNext.com"
        description="Read the Terms of Use for CallOfDutyNext.com — an independent Call of Duty fan news site. Understand your rights and responsibilities when using this site."
        keywords="terms of use, terms and conditions, CallOfDutyNext, fan site, user agreement, Call of Duty news"
        canonical="/terms"
        noIndex={false}
        schema={termsSchema}
      />
      <Navbar />

      <div className="pt-14">
        {/* Title bar */}
        <div className="border-b border-[#1e1e1e] bg-[#0d0d0d]">
          <div className="max-w-4xl mx-auto px-6 py-5">
            <div className="flex items-center gap-3 mb-1">
              <span className="font-['Barlow_Condensed'] font-bold text-xs tracking-[0.2em] text-[#555] uppercase border border-[#2a2a2a] px-2 py-0.5">
                Legal
              </span>
            </div>
            <h1 className="font-['Barlow_Condensed'] font-black text-white text-3xl uppercase tracking-wider leading-none">
              Terms of Use
            </h1>
            <p className="text-[#555] text-sm mt-1">
              Please read these terms carefully before using CallOfDutyNext.com.
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-6 py-10">

          {/* Intro box */}
          <div className="border border-[#2a2a2a] bg-[#0d0d0d] mb-8 p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center shrink-0 bg-[#1a1a1a] border border-[#2a2a2a]">
                <i className="ri-file-text-line text-[#00ff41] text-lg" />
              </div>
              <div>
                <p className="font-['Barlow_Condensed'] font-bold text-white text-base uppercase tracking-wide mb-2">
                  Agreement to Terms
                </p>
                <p className="text-[#888] text-sm leading-relaxed">
                  By accessing or using <strong className="text-[#aaa]">CallOfDutyNext.com</strong>, you agree to be bound by
                  these Terms of Use. If you do not agree with any part of these terms, you may not use this site.
                  This site is operated independently and is{' '}
                  <strong className="text-white">not affiliated with Activision Blizzard or Microsoft</strong>.
                </p>
              </div>
            </div>
          </div>

          {/* Sections */}
          <div className="border border-[#1e1e1e] divide-y divide-[#1e1e1e] mb-8">

            <Section title="Use of This Site">
              <p>
                CallOfDutyNext.com is a fan-operated, independent news and information resource.
                You are granted a limited, non-exclusive, non-transferable license to access and use this site
                for personal, non-commercial purposes only.
              </p>
              <p>
                You agree not to reproduce, duplicate, copy, sell, resell, or exploit any portion of this site's content
                without express written permission from the site operators.
              </p>
            </Section>

            <Section title="Intellectual Property">
              <p>
                All original editorial content, layout, and design on CallOfDutyNext.com is the intellectual property
                of this site&apos;s operators. You may not reproduce or redistribute our original content without prior consent.
              </p>
              <p>
                Game names, logos, artwork, and trademarks referenced on this site — including{' '}
                <strong className="text-[#ccc]">Call of Duty®</strong> — remain the property of their respective rights holders,
                including <strong className="text-[#ccc]">Activision Publishing, Inc.</strong> Their use on this site is
                strictly for informational and commentary purposes.
              </p>
            </Section>

            <Section title="User Conduct">
              <p>By using this site, you agree not to:</p>
              <ul className="list-none space-y-2">
                {[
                  'Post or transmit any content that is unlawful, harmful, threatening, or harassing',
                  'Attempt to gain unauthorized access to any part of the site or its infrastructure',
                  'Use automated tools, bots, or scrapers to harvest content without permission',
                  'Impersonate any person or entity, or misrepresent your affiliation with any organization',
                  'Engage in any conduct that restricts or inhibits other users from enjoying the site',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <span className="w-1 h-1 rounded-full bg-[#00ff41] mt-2 shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </Section>

            <Section title="Third-Party Links">
              <p>
                This site may contain links to third-party websites, including official Call of Duty channels,
                social media platforms, and community resources. These links are provided for convenience only.
              </p>
              <p>
                CallOfDutyNext.com has no control over the content or practices of third-party sites and accepts
                no responsibility for them. Visiting external links is entirely at your own risk.
              </p>
            </Section>

            <Section title="Disclaimer of Warranties">
              <p>
                This site and all content herein are provided on an <strong className="text-[#ccc]">&quot;as is&quot;</strong> and{' '}
                <strong className="text-[#ccc]">&quot;as available&quot;</strong> basis, without warranties of any kind — express or implied.
                We do not warrant that the site will be uninterrupted, error-free, or free of viruses or other harmful components.
              </p>
              <p>
                We make no guarantees as to the accuracy, completeness, or timeliness of any news, patch note information,
                or other content published on this site. All content is for informational and entertainment purposes only.
              </p>
            </Section>

            <Section title="Limitation of Liability">
              <p>
                To the fullest extent permitted by law, CallOfDutyNext.com and its operators shall not be liable
                for any indirect, incidental, special, consequential, or punitive damages arising from your use of — 
                or inability to use — this site or its content.
              </p>
              <p>
                This includes, but is not limited to, any reliance placed on content published here regarding
                game updates, patch notes, or other game-related information that may turn out to be inaccurate or outdated.
              </p>
            </Section>

            <Section title="Changes to These Terms">
              <p>
                We reserve the right to update or modify these Terms of Use at any time without prior notice.
                Changes will be effective immediately upon being posted on this page.
                Continued use of the site after any changes constitutes your acceptance of the new terms.
              </p>
            </Section>

            <Section title="Contact">
              <p>
                If you have any questions about these Terms of Use, please{' '}
                <Link to="/contact" className="text-[#00ff41] hover:underline cursor-pointer">
                  contact us
                </Link>.
              </p>
            </Section>

          </div>

          {/* Last updated */}
          <div className="flex items-center justify-between border border-[#1e1e1e] bg-[#0d0d0d] px-6 py-4 mb-8">
            <p className="text-[#555] text-xs tracking-wide uppercase font-['Barlow_Condensed'] font-bold">
              Last Updated
            </p>
            <p className="text-[#666] text-xs">March 2026</p>
          </div>

          {/* Nav links */}
          <div className="flex items-center gap-6">
            <Link
              to="/disclaimer"
              className="text-[#555] hover:text-white text-sm transition-colors cursor-pointer flex items-center gap-2"
            >
              <i className="ri-arrow-left-line text-xs" />
              Disclaimer
            </Link>
            <Link
              to="/contact"
              className="text-[#555] hover:text-white text-sm transition-colors cursor-pointer flex items-center gap-2"
            >
              Contact
              <i className="ri-arrow-right-line text-xs" />
            </Link>
          </div>

        </div>
      </div>

      <Footer />
    </div>
  );
}
